import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, KundeAkte, Unternehmen } from "@kundeninfo/api-client";
import { BearbeitenLeiste, Feld, darfAbbrechen, useFormularZustand, useRecordLock } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import type { SperrDienst } from "@kundeninfo/ui";
import { landName } from "../format.js";
import type { AircallSicht } from "../session.js";
import type { VerlassenWaechter } from "../router.js";
import { KundeBeziehungen } from "./KundeBeziehungen.js";
import { KundeAircallAccount } from "./KundeAircallAccount.js";
import { KundeKonditionen } from "./KundeKonditionen.js";
import { KundePortalMandanten } from "./KundePortalMandanten.js";
import "./KundeStammdaten.css";
import { KiKnopf } from "../components/meHuellen.js";
import { customerLogoSrc } from "./partners/LogoChoice.js";
import { LOGO_TEXT, PARTNER_TEXT } from "./partners/texts.js";
import { GeschaeftspartnerDialog, PARTNER_FIELDS, PARTNER_LABELS, changedFields, draftFrom, invoiceFormatText,
  masterFrom, usePartnerLeaveGuard } from "./partners/GeschaeftspartnerDialog.js";
import type { PartnerDraft } from "./partners/GeschaeftspartnerDialog.js";

/** Nur die Klientenmethoden, die diese Ansicht wirklich braucht — so lässt sie
 *  sich im Test mit einer Handvoll vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen.
 *  Die drei mittleren gehören dem Abschnitt „Zahlungskonditionen" (Aufgabe 10), die drei
 *  danach dem Abschnitt „Portal-Mandanten" (Mandantenabgleich mit dem Monitor,
 *  07.09.2026). Beide stehen unter den Stammdatenfeldern und werden deshalb über diese
 *  Verdrahtung mitversorgt. */
export type StammdatenClient =
  Pick<ApiClient, "updateCustomer" | "acquireLock" | "refreshLock" | "releaseLock"
    | "listLaender"
    | "listSelectablePaymentTerms" | "getRelationTerms" | "setRelationTerms"
    | "listExternalReferences" | "createExternalReference" | "deleteExternalReference"
    // Der Abschnitt „Unternehmenszuordnung" (Kleinpakete II, P1): anlegen und danach
    // die Akte frisch holen — die Antwort des Schreibwegs trägt nur die Nummer.
    // `setDebitorennummer` kam mit der Debitorennummer je Beziehung dazu (Aufgabe 9).
    | "addRelation" | "getCustomer" | "setDebitorennummer"
    // Rollen je Unternehmen und Kreditornummer (Lieferanten Strang A, Aufgabe 6).
    | "setRelationRoles" | "setKreditornummer"
    // Die Rechnungsangaben je Beziehung (Migration 053, E342) — samt Neuanlage einer Adresse.
    | "getRelationBilling" | "setRelationBilling" | "listAddresses" | "createAddress" | "updateAddress"
    // Die Ortszeile (Spec „Adresszeile", R17) der Stammdaten selbst und des Adresspanels der Neuanlage; `listLaender` steht oben.
    | "listPlzOrte"
    // Der Abschnitt „Aircall-Anbindung" (Paket „Abrechnungsquellen", 29.09.2026; Leitungen seit Migration 051).
    | "getAircallAccount" | "setzeAircallAccount" | "aircallAnbindungLeitungen" | "ordneAircallLeitungZu"
    | "beendeAircallLeitung" | "widerrufeAircallZuordnung" | "aircallAuswirkung"
    // Logo aus der Webseite (Entscheid 673): Kacheln holen hinter dem Feld „Webseite“, das gewählte Logo beim Speichern.
    | "lookupEnrichment" | "uploadCustomerLogo">;

/**
 * Alles außer dem Kunden selbst: die Verdrahtung, die von App über KundenAkte
 * hierher durchgereicht wird. `onAktualisiert` meldet jeden neuen Serverstand nach
 * oben (nach dem Speichern wie nach einer Konfliktauflösung), damit Kopf und
 * Kontextspalte nicht auf einem veralteten Satz sitzen bleiben.
 */
export type StammdatenUmfeld = {
  client: StammdatenClient;
  /** Ob die Übernahme einer fremden Sperre angeboten wird — LEAD/ADMIN, entschieden
   *  vom Aufrufer (siehe darfSperreUebernehmen in session.ts). */
  darfUebernehmen: boolean;
  /** Darf dieser Benutzer den Aircall-Account pflegen? Nur ADMIN (R7, Spec
   *  „Abrechnungsquellen"). Weglassbar — ohne Angabe sieht niemand die Knöpfe. */
  istAdmin?: boolean | undefined;
  /** Wie der Aircall-Block erscheint (E365): `verbergen` ohne Block, `hinweis` mit dem Text
   *  zur Anbindung darüber (ADMIN außerhalb des Aircall-Unternehmens). */
  aircall: { sicht: AircallSicht; hinweis: string | null };
  onAktualisiert(akte: KundeAkte): void;
  registriereVerlassenWaechter(frage: VerlassenWaechter): () => void;
};

export type KundeStammdatenProps = StammdatenUmfeld & {
  kunde: KundeAkte;
  /**
   * Die sichtbaren Konzernunternehmen — nur für den Abschnitt „Unternehmenszuordnung"
   * unten. WAHLFREI: Ohne Liste gibt es nichts zuzuordnen, der Abschnitt zeigt dann nur
   * den Satz. So bleiben die Tests, die diese Ansicht ohne Sitzung zeichnen,
   * unverändert gültig.
   */
  companies?: readonly Unternehmen[];
};

/**
 * `conflict.current` kommt als unknown über die Leitung (die API kann seine Gestalt im
 * Schema nicht versprechen, siehe openapi.ts). Tatsächlich liefert der Fehlerhandler den
 * vollen Kundensatz; hier wird das geprüft statt blind behauptet — fehlt die Gestalt,
 * zeigt der Konfliktkasten nur die Meldung ohne Übernahmeangebot.
 */
function alsAkte(wert: unknown): KundeAkte | null {
  if (typeof wert !== "object" || wert === null) return null;
  const k = wert as Partial<KundeAkte>;
  if (typeof k.id !== "string" || typeof k.companyName !== "string"
    || typeof k.version !== "number") return null;
  return wert as KundeAkte;
}

const KONFLIKT_ZEIT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});

function formatKonfliktZeit(iso: string): string {
  const zeitpunkt = new Date(iso);
  if (Number.isNaN(zeitpunkt.getTime())) return iso;
  return `${KONFLIKT_ZEIT.format(zeitpunkt)} Uhr`;
}

type Konflikt = { von: string; wann: string; fremd: KundeAkte | null };

/** Was ein Speichern ergibt: der neue Stand und — nur wenn das gewählte Logo scheiterte — der Grund dafür. */
type SaveResult = { akte: KundeAkte; logoError: string | null };

/** Ein Wert zum Lesen: das Land mit seinem deutschen Namen, das Rechnungsformat mit seiner Beschriftung — in der
 *  Leseansicht wie im Vergleich des fremden Stands. */
function anzeige(feld: keyof PartnerDraft, wert: string): string {
  if (feld === "country") return landName(wert);
  if (feld === "invoiceFormat") return invoiceFormatText(wert);
  return wert;
}

/**
 * Der Inhalt des Tabs „Stammdaten" in beiden Modi — die erste Schreibfunktion des
 * Systems. Im Lesemodus stehen nur Werte (Feld mit bearbeitbar={false}); „Bearbeiten"
 * meldet den Wunsch bei useRecordLock an, und erst wenn die Sperre wirklich GEHALTEN
 * ist, öffnet sich der Dialog. Schlägt der Erwerb mit 423 fehl, bleibt der Lesemodus
 * mit dem Hinweisband der Leiste stehen; der Haken beobachtet dann alle 15 s und
 * öffnet die Bearbeitung von selbst, sobald die fremde Sperre fällt (der Wunsch
 * bleibt ja angemeldet).
 *
 * Gegen verlorene Änderungen schützt der Versionsvergleich beim Speichern, nicht die
 * Sperre (Entscheidung AA) — deshalb wirft ein Speichern trotz gehaltener Sperre
 * unter Umständen eine 409, und deren Rumpf trägt den aktuellen Serverstand.
 *
 * **Wo die Bearbeitung steht** (Entscheide 814–819): im gemeinsamen Geschäftspartner-Dialog im Modus `bearbeiten` —
 * dieselbe Maske wie beim Anlegen, rechts angeschlagen, mit genau zwei Ausgängen im Fuss. Dahinter bleiben die Werte
 * lesbar stehen — sie sind der Zusammenhang, in dem gerade geändert wird. Nur hier (819): die Sperrleiste im Panel und
 * der Hinweis „fremder Stand“; Beziehungen pflegt man weiter in der Akte (`KundeBeziehungen`).
 *
 * Die SPERRE wandert dabei nicht mit: Sie hängt weiterhin an `wunsch`, nicht am Panel.
 * Das ist der Grund, warum die Leiste zweimal vorkommt und nie zugleich — draussen im
 * Lesezustand (mit „Bearbeiten", dem FREMD-Band, „Übernehmen" und „Nicht mehr warten"),
 * drinnen während der Bearbeitung (dann NUR mit dem Störungsband, `aktionenImPanel`).
 * Der Wartezustand ist keine Bearbeitung: Dort steht kein Panel, und die Leiste draussen
 * behält jeden ihrer Wege.
 */
export function KundeStammdaten(p: KundeStammdatenProps): ReactElement {
  // Der zuletzt bekannte Serverstand — Quelle für Lesemodus UND für die Version beim
  // Speichern. Nach einem Erfolg steht hier die Antwort (mit ihrer neuen Version);
  // der Effekt hält ihn nach, wenn die Ebene darüber die Akte austauscht.
  const [stand, setStand] = useState(p.kunde);
  useEffect(() => { setStand(p.kunde); }, [p.kunde]);

  /** Der Benutzer will bearbeiten — treibt useRecordLock (aktiv) und bleibt auch bei
   *  FREMD bestehen, damit die Beobachtung weiterläuft. */
  const [wunsch, setWunsch] = useState(false);
  /** Die Felder sind offen. Wird erst wahr, wenn die Sperre GEHALTEN ist, und bleibt
   *  bei einem vorübergehenden Herzschlag-FEHLER bewusst wahr (Eingaben bleiben). */
  const [offen, setOffen] = useState(false);
  const [entwurf, setEntwurf] = useState<PartnerDraft>(() => draftFrom(p.kunde));
  /** Das im Markenrechts-Dialog gewählte Logo (Entscheid 673) — Teil des Entwurfs, erst „Speichern“ lädt es hoch. */
  const [chosenLogo, setChosenLogo] = useState<string | null>(null);
  /** Meldung eines gescheiterten SPERR-Erwerbs (Lesezustand). Fehler des Speicherns führt
   *  `formular.meldung` (im Panel). */
  const [meldung, setMeldung] = useState<string | null>(null);
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  /** „Meine Eingaben behalten“ geht über `formular.neuLaden()` (der einzige Weg aus dem
   *  fremden Stand) — diese Markierung sagt dem Laden, dass es die Eingaben NICHT ersetzt,
   *  sondern nur die Versionsbasis vorrückt. */
  const eingabenBehalten = useRef(false);

  /**
   * Die Formularzustände (ME-Etappe 3, Aufgabe 3): `laeuft` sperrt die Felder während des
   * Speicherns, eine 422 legt die Befunde an die Felder (Sprung zum ersten), eine 409 setzt
   * das Formular in den „fremden Stand“ — gesperrt, die Eingaben bleiben stehen, daneben
   * der Vergleich beider Stände (Review-Fokus 2). Erst „Neu laden“ (fremden Stand
   * übernehmen) oder „Meine Eingaben behalten“ geben es wieder frei.
   */
  const formular = useFormularZustand<SaveResult>({
    speichern: async () => {
      let akte: KundeAkte;
      try {
        akte = await p.client.updateCustomer(stand.id, stand.version, masterFrom(entwurf));
      } catch (fehler) {
        // Den mitgelieferten Serverstand der 409 festhalten — der Haken reicht ihn nicht
        // durch, der Vergleich und „Neu laden“ brauchen ihn.
        if (fehler instanceof ApiError && fehler.status === 409) {
          setKonflikt(fehler.conflict !== undefined
            ? { von: fehler.conflict.changedBy, wann: fehler.conflict.changedAt,
                fremd: alsAkte(fehler.conflict.current) }
            : { von: "", wann: "", fremd: null });
        }
        throw fehler;
      }
      // Das Logo NACH den Stammdaten (Entscheid 673): Eine 409 oben lädt nichts hoch. Scheitert nur das Logo, sind
      // die Stammdaten trotzdem gespeichert — das ist kein Fehler des Speicherns, sondern ein Ergebnis mit Grund
      // (Muster der Anlege-Maske); die Logo-Route springt die Version nicht, ihre Antwort ist der neue Stand.
      if (chosenLogo === null) return { akte, logoError: null };
      try {
        return { akte: await p.client.uploadCustomerLogo(akte.id, chosenLogo), logoError: null };
      } catch (fehler) {
        return { akte, logoError: fehler instanceof ApiError ? fehler.message : LOGO_TEXT.unknownError };
      }
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      // Ohne mitgelieferten Stand wird der Kunde frisch geholt — auch für „Meine Eingaben
      // behalten“: Sonst rückte die Version nicht vor, und das nächste Speichern liefe
      // sofort wieder in die 409 (Fixrunde 1, H2).
      const fremd = konflikt?.fremd ?? await p.client.getCustomer(stand.id);
      setStand(fremd);
      p.onAktualisiert(fremd);
      // „Neu laden“ übernimmt den fremden Stand: Mit den Eingaben fällt auch die Logo-Wahl (Teil des Entwurfs).
      if (!behalten) { setEntwurf(draftFrom(fremd)); setChosenLogo(null); }
      setKonflikt(null);
    },
    bekannteFelder: PARTNER_FIELDS.map((f) => f.name),
    // Sammelmeldung mit den sichtbaren Beschriftungen statt technischer Feldnamen
    // (Gesamtreview Etappe 3: einzige Maske der Stichprobe ohne Karte).
    beschriftungen: PARTNER_LABELS,
  });
  const speichert = formular.laeuft;

  // Der Adapter auf den Client: die Fehlerform (423 mit locked: { lockedBy, since })
  // ist bereits deckungsgleich mit dem, was useRecordLock erwartet (Fixrunde 2, Fund 3
  // in useRecordLock.ts) — hier wird deshalb NUR benannt, nicht umgeformt. Die instabile
  // Objektidentität pro Render ist unschädlich: der Haken hält den Dienst in einer Ref.
  const dienst: SperrDienst = {
    acquire: (ziel, force) => p.client.acquireLock(ziel, force),
    refresh: (ziel) => p.client.refreshLock(ziel),
    release: (ziel) => p.client.releaseLock(ziel),
  };
  const { zustand, uebernehmen } = useRecordLock({
    dienst,
    ziel: { entityType: "customer", entityId: stand.id },
    aktiv: wunsch,
  });

  // Sobald die Sperre hält, öffnen sich die Felder — beim direkten Erwerb wie nach
  // einer Übernahme oder wenn die Beobachtung eine gefallene fremde Sperre ergattert.
  const sperrArt = zustand.art;
  useEffect(() => {
    if (wunsch && sperrArt === "GEHALTEN") setOffen(true);
  }, [wunsch, sperrArt]);

  // Scheitert schon der Erwerb technisch (nicht 423), wäre die Leiste stumm: ihr
  // FEHLER-Band zeigt sie nur während der Bearbeitung. Deshalb hier die Meldung und
  // zurück in den Ruhezustand — ein erneuter Klick auf „Bearbeiten" versucht es wieder.
  useEffect(() => {
    if (wunsch && !offen && zustand.art === "FEHLER") {
      setMeldung(zustand.meldung);
      setWunsch(false);
    }
  }, [wunsch, offen, zustand]);

  // Verlassen-Wächter: nur registriert, solange offene UND geänderte Eingaben da sind.
  // Er deckt programmatische Navigation und die Zurück-Taste gleichermaßen (useRoute).
  const gespeichert = draftFrom(stand);
  const geaendert = offen && (changedFields(entwurf, gespeichert).length > 0 || chosenLogo !== null);
  usePartnerLeaveGuard(geaendert, p.registriereVerlassenWaechter);

  function beiBearbeiten(): void {
    setEntwurf(draftFrom(stand));
    setChosenLogo(null);
    setMeldung(null);
    formular.zuruecksetzen();
    setKonflikt(null);
    setWunsch(true);
  }

  /**
   * Der EINE Ausgang des Panels neben dem Speichern (Hausregel „striktes Modalpanel",
   * 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage bei ungespeicherten
   * Eingaben. Knopf, Kreuz und Escape gehen durch diese Funktion.
   *
   * Dahinter steckt derselbe Rückbau wie beim früheren „Verwerfen" — samt der Freigabe der
   * Sperre. Verlorenginge sonst nicht nur die Eingabe: Ein Abbrechen, das die Sperre stehen
   * liesse, sperrte den Datensatz für alle anderen aus.
   */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    beiVerwerfen();
  }

  /**
   * Auch der Rückzug aus dem Wartezustand („Nicht mehr warten“, 2a-Kleinpunkt W1): Der Wunsch wird zurückgesetzt,
   * damit useRecordLock `aktiv:false` sieht und seine FREMD-Beobachtung abräumt — sonst risse sie dem Benutzer später
   * ungefragt die Bearbeitung auf. Im Wartezustand ist nichts geöffnet und nichts eingegeben.
   */
  function beiVerwerfen(): void {
    setEntwurf(draftFrom(stand));
    setChosenLogo(null);
    setMeldung(null);
    formular.zuruecksetzen();
    setKonflikt(null);
    setOffen(false);
    setWunsch(false); // gibt die Sperre über useRecordLock frei
  }

  async function beiSpeichern(): Promise<void> {
    const ergebnis = await formular.aktion();
    if (ergebnis === undefined) return; // Befund steht am Feld, im Hinweis oder im Formular
    const antwort = ergebnis.akte;
    // Version und Werte aus der ANTWORT übernehmen — mit der alten Version
    // scheiterte das nächste Speichern sofort am Versionsvergleich.
    setStand(antwort);
    setEntwurf(draftFrom(antwort));
    setChosenLogo(null);
    setKonflikt(null);
    setOffen(false);
    setWunsch(false); // Sperre frei
    // Nur das Logo scheiterte: Die Meldung steht im Lesezustand, wo das Panel eben war.
    if (ergebnis.logoError !== null) setMeldung(LOGO_TEXT.savedWithoutLogo(ergebnis.logoError));
    p.onAktualisiert(antwort);
  }

  /** Konfliktauflösung 2: die eigene Eingabe behalten. Die Versionsbasis rückt auf den
   *  Serverstand vor — das nächste Speichern überschreibt den fremden Stand dann
   *  BEWUSST (der Hinweis hat beide Stände gezeigt). Konfliktauflösung 1 („Neu laden“,
   *  fremden Stand übernehmen) ist der Knopf in der Hinweiszeile des Formulars. */
  function eigeneEingabenBehalten(): void {
    eingabenBehalten.current = true;
    void formular.neuLaden();
  }

  const modus = offen ? (speichert ? "SPEICHERT" : "BEARBEITEN") : "LESEN";

  /**
   * Die Leiste — mit ihren Rückrufen an einer Stelle, weil sie an ZWEI Orten vorkommt:
   * draussen im Lesezustand und drinnen im Panel (siehe Kopfkommentar). Nie zugleich.
   */
  function leiste(imPanel: boolean): ReactElement {
    return (
      <BearbeitenLeiste
        modus={modus}
        sperre={zustand}
        darfUebernehmen={p.darfUebernehmen}
        {...(imPanel ? { aktionenImPanel: true } : {})}
        onBearbeiten={beiBearbeiten}
        onSpeichern={() => { void beiSpeichern(); }}
        onVerwerfen={beiVerwerfen}
        onUebernehmen={() => { void uebernehmen(); }}
        onNichtMehrWarten={beiVerwerfen}
      />
    );
  }

  /**
   * Die Meldung gehört dorthin, wo gerade gearbeitet wird: Ein gescheiterter SPERR-Erwerb
   * steht im Lesezustand, ein gescheitertes SPEICHERN im Panel — dort, wo die Eingabe
   * steht, die stehen geblieben ist.
   */
  function meldungsband(text: string | null): ReactElement | null {
    return text === null ? null : <Hinweis art="fehler">{text}</Hinweis>;
  }

  return (
    <div className="ks">
      {!offen && leiste(false)}
      {!offen && meldungsband(meldung)}

      {/* Die Werte stehen IMMER lesbar da — auch hinter dem offenen Panel: Sie sind der
          Zusammenhang, in dem gerade geändert wird. Im Lesemodus zeichnet `Feld` sie als
          reinen Text, ohne `<input>` und ohne `id` — deshalb kollidieren sie mit den
          Eingabefeldern des Panels weder in der Kennung noch für den Bildschirmleser. */}
      <div className="ks__felder">
        {PARTNER_FIELDS.map((f) => (
          <Feld key={f.name} id={`ks-lesen-${f.name}`} label={f.label}
            wert={anzeige(f.name, gespeichert[f.name])} bearbeitbar={false} onChange={() => {}} />
        ))}
      </div>

      {/* Die Zahlungskonditionen stehen UNTER den Stammdaten, nicht zwischen ihnen: Sie
          gehören der Kundenbeziehung, nicht der Firma, und werden getrennt gespeichert
          (eigene Version, eigener Schreibweg) — die Bearbeitung oben fasst sie nicht an.
          Gezeigt wird `stand`, nicht `p.kunde`: Nach einem Speichern der Stammdaten steht
          dort der frische Serverstand. */}
      <KundeKonditionen client={p.client} relations={stand.relations} verdeckt={offen} />

      {/* Und darunter die PORTAL-MANDANTEN — ein EIGENER Block, kein Teil der Stammdaten:
          Die Zuordnungen sind eigene Sätze, die angelegt und entfernt werden, nicht
          mitgespeichert. Sie hängen deshalb nicht am Speichern-Knopf oben und brauchen
          keine Satzsperre (zwei Bearbeiter kommen sich nicht in die Quere; meinen sie
          denselben Mandanten, entscheidet der eindeutige Schlüssel in der Datenbank).

          `stand.id` und nicht `p.kunde.id`: derselbe Grund wie beim Konditionen-Abschnitt
          darüber — nach einem Speichern steht in `stand` der frische Serverstand. */}
      <KundePortalMandanten client={p.client} customerId={stand.id} verdeckt={offen} />

      {/* Der AIRCALL-ACCOUNT (Spec „Abrechnungsquellen") — ein EIGENER Block neben den
          Portal-Mandanten, aus demselben Grund: ein eigener Satz mit eigener Version,
          nicht Teil der Stammdaten. Pflegen darf nur ADMIN; lesen jeder im Aircall-Unternehmen,
          ADMIN überall mit Hinweis auf die Anbindung (Mandantentrennung E365). */}
      {p.aircall.sicht === "hinweis" && p.aircall.hinweis !== null
        && <Hinweis art="info">{p.aircall.hinweis}</Hinweis>}
      {p.aircall.sicht !== "verbergen" && (
        <KundeAircallAccount client={p.client} customerId={stand.id} leitungenClient={p.client}
          kundeName={stand.companyName} istAdmin={p.istAdmin === true} verdeckt={offen} />
      )}

      {/* Und darunter die UNTERNEHMENSZUORDNUNG (Kleinpakete II, P1) — wieder ein
          EIGENER Block aus demselben Grund wie die beiden darüber: Eine Kundenbeziehung
          ist ein eigener Satz, der angelegt wird, nicht ein Feld, das mitgespeichert
          wird. `stand` und nicht `p.kunde`: Nach einem Speichern der Stammdaten steht
          dort der frische Serverstand. */}
      <KundeBeziehungen client={p.client} kunde={stand} companies={p.companies ?? []}
        verdeckt={offen}
        onAktualisiert={(akte) => { setStand(akte); p.onAktualisiert(akte); }} />

      {offen && (
        <GeschaeftspartnerDialog modus="bearbeiten" titel={PARTNER_TEXT.editTitle(stand.companyName)}
          client={p.client} draft={entwurf} setDraft={setEntwurf} base={gespeichert}
          fieldErrors={formular.feldFehler} formularProps={formular.formularProps}
          gesperrt={formular.zustand !== "bereit"}
          // Der innere Stand der Ortszeile gehört zu Datensatz UND Fassung: „Neu laden“ und „Meine Eingaben
          // behalten“ rücken die Version vor und beginnen die Zeile neu (M-6a).
          ortszeileKey={`${stand.id}:${stand.version}`}
          logo={{ chosen: chosenLogo, onChosen: setChosenLogo, existing: customerLogoSrc(stand) }}
          hinweis={konfliktKasten()}
          // Die Sperrleiste im Panel (819, nur mit ihrem Störungsband) und die Meldung eines gescheiterten Speicherns
          // — im Slot des Panels, sie rollen nicht mit dem Inhalt davon.
          meldungen={<>{leiste(true)}{meldungsband(formular.meldung)}</>}
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}
          fuss={(
            <>
              {/* SPEICHERN IST GESPERRT, SOLANGE DIE SPERRE FREMD IST (Ruling zur Fixrunde 1, 07.09.2026) — eine bewusste
                  Verschärfung gegenüber Entscheidung AA: Neben einem Band, das „X bearbeitet diesen Datensatz" sagt,
                  gehört kein scharfer Speichern-Knopf. Wer trotzdem muss, ÜBERNIMMT die Sperre — oder bricht ab. */}
              <KiKnopf art="primaer" deaktiviert={formular.zustand !== "bereit" || zustand.art === "FREMD"}
                onClick={() => { void beiSpeichern(); }}>{PARTNER_TEXT.save}</KiKnopf>
              <KiKnopf deaktiviert={speichert} onClick={() => { void abbrechen(); }}>{PARTNER_TEXT.cancel}</KiKnopf>
            </>
          )} />
      )}
    </div>
  );

  /**
   * Der Text der Hinweiszeile „fremder Stand“ (Slot `hinweis` des Formulars): wer wann
   * geändert hat, der Vergleich beider Stände (sichtbare Zusammenfassung — nichts geht
   * still verloren, Review-Fokus 2) und der zweite Weg „Meine Eingaben behalten“. Der erste
   * Weg, den fremden Stand zu übernehmen, ist „Neu laden“ des Formulars.
   */
  function konfliktKasten(): ReactElement | undefined {
    if (konflikt === null) return undefined;
    const fremd = konflikt.fremd === null ? null : draftFrom(konflikt.fremd);
    // ALLE Felder — stünde hier eine kürzere Liste, verschwände ein Konflikt an Land oder Leitweg-ID unsichtbar.
    const zeige = (feld: keyof PartnerDraft, wert: string): string => (wert.trim() === "" ? "—" : anzeige(feld, wert));
    return (
      <div>
        {konflikt.von !== ""
          ? PARTNER_TEXT.conflictBy(konflikt.von, formatKonfliktZeit(konflikt.wann))
          : PARTNER_TEXT.conflictUnknown}
        {" "}{PARTNER_TEXT.conflictCompare}
        {fremd !== null && (
          <dl className="ks__konflikt-vergleich">
            {changedFields(entwurf, fremd).map((name) => (
              <div key={name}>
                <dt>{PARTNER_LABELS[name]}</dt>
                <dd>{PARTNER_TEXT.conflictMine(zeige(name, entwurf[name]))}</dd>
                <dd>{PARTNER_TEXT.conflictTheirs(zeige(name, fremd[name]))}</dd>
              </div>
            ))}
          </dl>
        )}
        {" "}
        <KiKnopf className="ki-eingaben-behalten" onClick={eigeneEingabenBehalten}>{PARTNER_TEXT.keepMine}</KiKnopf>
      </div>
    );
  }
}
